// frontend/src/api/api.js
// Cliente centralizado para la API del backend.
// AHORA con un manejador de errores global para el código de estado 401.

import { useAuthStore } from '../store/authStore';
import { getEntraSession } from '../lib/entraAuth';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const readResponseBody = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();

  if (!text) return null;

  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return { message: text };
    }
  }

  return { message: text };
};

const extractApiMessage = (body, response) => {
  return body?.error?.message
    || body?.message
    || `Error en la petición: ${response.status} ${response.statusText}`;
};

/**
 * @function handleApiError
 * @description Función centralizada para manejar errores de respuesta de la API.
 * Si el error es un 401, cierra la sesión del usuario.
 * @param {Response} response - El objeto de respuesta de la API.
 * @throws {Error} - Lanza un error con el mensaje del backend.
 */
const handleApiError = async (response) => {
  // Si el status es 401, la sesión es inválida o ha expirado.
  if (response.status === 401) {
    // Usamos el store de Zustand para cerrar la sesión globalmente.
    useAuthStore.getState().clearAuthData();
    // Lanzamos un error para detener la ejecución actual.
    throw new Error('Sesión inválida o expirada. Por favor, inicie sesión de nuevo.');
  }

  const errorData = await readResponseBody(response);
  const errorMessage = extractApiMessage(errorData, response);
  throw new Error(errorMessage);
};


/**
 * @function request
 * @description Realiza una petición HTTP al backend para datos JSON.
 * @param {string} endpoint - La ruta de la API (ej: '/api/auth/login').
 * @param {string} method - El método HTTP (GET, POST, PUT, DELETE).
 * @param {object} [data=null] - Los datos a enviar en el cuerpo de la petición.
 * @param {string} [token=null] - Token de autorización JWT si es necesario.
 * @returns {Promise<object>} - La respuesta de la API.
 * @throws {Error} - Si la petición falla o la respuesta indica un error.
 */
export async function request(endpoint, method = 'GET', data = null, token = null) {
  const headers = {
    'Content-Type': 'application/json',
  };

  let resolvedToken = token || useAuthStore.getState().token;

  if (!resolvedToken) {
    try {
      const session = await getEntraSession();
      resolvedToken = session?.token || session?.accessToken || useAuthStore.getState().token || null;
    } catch (error) {
      resolvedToken = useAuthStore.getState().token || null;
    }
  }

  if (resolvedToken) {
    headers['Authorization'] = `Bearer ${resolvedToken}`;
  }

  const config = {
    method,
    headers,
  };

  if (data) {
    config.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      // Usamos el nuevo manejador de errores centralizado.
      await handleApiError(response);
    }

    if (response.status === 204) {
      return {};
    }

    return await readResponseBody(response);
  } catch (error) {
    console.error(`Error en la petición a ${endpoint}:`, error.message);
    throw error;
  }
}

/**
 * @function fileRequest
 * @description Realiza una petición HTTP al backend para subir archivos (FormData).
 * @param {string} endpoint - La ruta de la API.
 * @param {FormData} formData - El objeto FormData con el archivo.
 * @param {string} token - Token de autorización JWT.
 * @returns {Promise<object>} - La respuesta de la API.
 * @throws {Error} - Si la petición falla o la respuesta indica un error.
 */
export async function fileRequest(endpoint, formData, token) {
  let resolvedToken = token || useAuthStore.getState().token;

  if (!resolvedToken) {
    try {
      const session = await getEntraSession();
      resolvedToken = session?.token || session?.accessToken || useAuthStore.getState().token || null;
    } catch (error) {
      resolvedToken = useAuthStore.getState().token || null;
    }
  }

  const headers = {};

  if (resolvedToken) {
    headers.Authorization = `Bearer ${resolvedToken}`;
  }

  const config = {
    method: 'POST',
    headers,
    body: formData,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      // Usamos el nuevo manejador de errores centralizado también aquí.
      await handleApiError(response);
    }

    return await readResponseBody(response);
  } catch (error) {
    console.error(`Error en la subida de archivo a ${endpoint}:`, error.message);
    throw error;
  }
}


// Exporta todas las funciones de los módulos de API
export * from './auth';
export * from './client';
export * from './project';
export * from './dashboard';
export * from './user';
export * from './backup';
export * from './template'; // NUEVO: Exportar las funciones de la API de plantillas
